/* Whiteboard shared utilities & element factories.
 *
 * Coordinate system: world-space (CANVAS_W × CANVAS_H is a reference plane; elements
 * may lie outside for the infinite-canvas feel). The viewport (pan + zoom) determines
 * which slice of world-space renders inside the SVG.
 */

export const CANVAS_W = 1600;
export const CANVAS_H = 1000;

export const COLORS = [
  "#0A0C10", // ink
  "#EF4444", // red
  "#F59E0B", // amber
  "#10B981", // green
  "#0EA5E9", // sky
  "#7C3AED", // violet
  "#EC4899", // pink
];

export const STICKY_TINTS = ["#FEF08A", "#FBCFE8", "#BFDBFE", "#BBF7D0", "#FED7AA"];

export const STROKE_WIDTHS = [2, 4, 6, 10, 16];

export const rid = () => Math.random().toString(36).slice(2, 10);

export const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Convert client px → world coords using the SVG bounding rect + view state.
export function clientToWorld(evt, svgEl, view) {
  const r = svgEl.getBoundingClientRect();
  const cx = evt.touches?.[0]?.clientX ?? evt.clientX;
  const cy = evt.touches?.[0]?.clientY ?? evt.clientY;
  const nx = (cx - r.left) / r.width;   // 0..1 of the viewbox slice
  const ny = (cy - r.top) / r.height;
  return {
    x: view.panX + nx * (CANVAS_W / view.zoom),
    y: view.panY + ny * (CANVAS_H / view.zoom),
  };
}

// Element factories (world coords)
export const newRect = (x = 320, y = 260, color = COLORS[0]) => ({
  id: rid(), type: "rectangle", x, y, width: 240, height: 100, label: "", color, fontSize: 18,
});
export const newEllipse = (x = 320, y = 260, color = COLORS[1]) => ({
  id: rid(), type: "ellipse", x, y, width: 240, height: 100, label: "", color, fontSize: 18,
});
export const newText = (x = 320, y = 200, color = COLORS[0]) => ({
  id: rid(), type: "text", x, y, text: "", fontSize: 24, color, width: 420, height: 120,
});
export const newArrow = (x = 320, y = 300, color = COLORS[0]) => ({
  id: rid(), type: "arrow", x, y, endX: x + 200, endY: y, label: "", color,
});
export const newImage = (src, x = 320, y = 260, width = 320, height = 240, title = "") => ({
  id: rid(), type: "image", x, y, width, height, src, title,
});
export const newSticky = (x = 340, y = 300, tint = STICKY_TINTS[0]) => ({
  id: rid(), type: "sticky", x, y, width: 220, height: 200, label: "Note…", color: tint, fontSize: 16,
});
export const newMath = (x = 340, y = 220) => ({
  id: rid(), type: "math", x, y, width: 420, height: 90, text: "$E = mc^2$", fontSize: 24, color: "#0A0C10",
});

// Merge remote element into local list (respects tombstones)
export function mergeRemoteElement(list, remote) {
  const filtered = list.filter((e) => e.id !== remote.id);
  if (remote.deleted) return filtered;
  return [...filtered, remote];
}

// Convert an element's coord bag into the payload the API accepts
export function elementToPayload(el) {
  return {
    id: el.id,
    type: el.type,
    x: el.x || 0,
    y: el.y || 0,
    width: el.width || 0,
    height: el.height || 0,
    endX: el.endX || 0,
    endY: el.endY || 0,
    label: el.label || "",
    text: el.text || "",
    color: el.color || "#0A0C10",
    fontSize: el.fontSize || 22,
    src: el.src || "",
    title: el.title || "",
    ...(el.type === "prop" ? { propType: el.propType || "", state: el.state || {} } : {}),
  };
}
